// Browser environment detection for the Free Tier install hints.
// The detect* functions are pure so they can be unit-tested against real
// user-agent strings (see /app/scripts/test_env_detection.mjs).

// Apps that brand their webview. Covers the mail apps the user cares about
// (Outlook, Yahoo, the Google app) plus the usual social webviews.
const IN_APP_TOKENS = /(FBAN|FBAV|FB_IAB|FBIOS|FBDV|Instagram|LinkedInApp|Snapchat|Pinterest|BytedanceWebview|musical_ly|TikTok|MicroMessenger|WhatsApp|Twitter|GSA\/|Outlook-(iOS|Android)|OutlookMobile|YahooMobileMail|YahooMail|YJApp|\bLine\/\d)/i;

// Real third-party browsers on iOS. They are not in-app webviews. Since iOS
// 16.4 they can all add to the home screen too (via the Share sheet or their
// own menu -> Share), so they get install instructions just like Safari.
// This also covers the common case where Gmail hands a link off to Chrome
// when Chrome is installed on the device.
const IOS_BROWSER_TOKENS = /(CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo)/i;

const IOS_BROWSER_NAMES = [
  ['chrome', /CriOS/i],
  ['firefox', /FxiOS/i],
  ['edge', /EdgiOS/i],
  ['opera', /OPiOS|OPT\//i],
  ['duckduckgo', /DuckDuckGo/i],
];

export const isIOSDevice = ({ ua, maxTouchPoints = 0 }) =>
  /iPad|iPhone|iPod/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1);

// `hasStandaloneFlag` is `'standalone' in navigator`. Safari on iOS defines it;
// an in-app WKWebView does not. That is the only reliable signal for mail apps
// such as Gmail and Yahoo, whose webviews send an unmodified Safari UA.
export function detectInApp(env) {
  if (IN_APP_TOKENS.test(env.ua)) return true;
  if (/Android/i.test(env.ua) && /;\s*wv\)/i.test(env.ua)) return true;
  if (isIOSDevice(env) && !IOS_BROWSER_TOKENS.test(env.ua) && !env.hasStandaloneFlag) return true;
  return false;
}

export function detectIosSafari(env) {
  return (
    isIOSDevice(env) &&
    !IOS_BROWSER_TOKENS.test(env.ua) &&
    Boolean(env.hasStandaloneFlag) &&
    !detectInApp(env)
  );
}

// Any real iOS browser (Safari or a third-party one) that isn't a trapped
// in-app webview. All of these support "Add to Home Screen" on iOS 16.4+.
export function detectIosInstallable(env) {
  return isIOSDevice(env) && !detectInApp(env);
}

export function iosBrowserName(ua) {
  const match = IOS_BROWSER_NAMES.find(([, re]) => re.test(ua));
  return match ? match[0] : 'safari';
}

// Firefox/Edge/Opera hide the Share action behind their own menu button first;
// Safari, Chrome and DuckDuckGo expose Share directly in the toolbar.
export function getIosInstallCopy(browserName) {
  if (browserName === 'firefox' || browserName === 'edge' || browserName === 'opera') {
    return "Add this to your home screen: open your browser menu, tap Share, then Add to Home Screen.";
  }
  return 'Add this to your home screen: tap Share, then Add to Home Screen.';
}

const readEnv = () => ({
  ua: navigator.userAgent || '',
  hasStandaloneFlag: 'standalone' in navigator,
  maxTouchPoints: navigator.maxTouchPoints || 0,
});

export const isInAppBrowser = () => detectInApp(readEnv());
export const isIosSafari = () => detectIosSafari(readEnv());
export const isIosInstallable = () => detectIosInstallable(readEnv());
export const getIosBrowserName = () => iosBrowserName(navigator.userAgent || '');

export const isStandaloneApp = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.matchMedia('(display-mode: fullscreen)').matches ||
  window.navigator.standalone === true;

export const INAPP_HINT_KEY = 'free_hint_inapp_dismissed';
export const INSTALL_HINT_KEY = 'free_hint_install_dismissed';

export const isHintDismissed = (key) => localStorage.getItem(key) === '1';
export const dismissHint = (key) => localStorage.setItem(key, '1');

// ?hint=inapp | ios | android — forces a bar on screen for visual checking.
export const forcedHint = () => new URLSearchParams(window.location.search).get('hint');
